import dayjs from 'dayjs';

import type { ChipIntent } from '@/components/common/chip';
import { formatPrice } from '@/lib/format';

import { applicabilitySchema } from './schemas';
import type {
  Applicability,
  ApplicabilityFormValue,
  ApplicableTo,
  DiscountFormat,
  DiscountStatus,
  DiscountTier,
  QuantityRuleType,
  RedemptionStatus,
  TriggerFormValue,
  TriggerType,
} from './types';

/** Flat amounts: whole values show no cents (`$120`, `$300`) to match the discount Figma
 *  (listing column + detail card); fractional values keep 2 dp (`$99.50`). */
const currencyWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const currencyCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "Maximum Discount Limit" / value cell: `$120` for flat, `24%` for percentage.
 *  `value` is in the discount's natural unit (dollars for flat, 0–100 for percentage).
 *  NOTE: assumes flat `value` is dollars, not cents — confirm against a live payload. */
export function formatDiscountValue(value: number, format: DiscountFormat): string {
  if (format === 'percentage') return `${value}%`;
  return Number.isInteger(value) ? currencyWhole.format(value) : currencyCents.format(value);
}

export const DISCOUNT_FORMAT_LABEL: Record<DiscountFormat, string> = {
  flat_amount: 'Flat Amount',
  percentage: 'Percentage',
};

export const APPLICABLE_TO_LABEL: Record<ApplicableTo, string> = {
  device: 'Device',
  bundle: 'Bundle',
  customer: 'Customer',
};

/** Status pill intents — the SHIPPED `<Chip>` intents render the exact Figma colorways via
 *  design tokens (verified against node 652-228693 + variable defs; mirrors the customers
 *  feature which solved the identical green/pink pattern):
 *    active → approved (#A7F0BA/#3D5B34 green) · inactive → attention (#FFDFDF/#A2191F pink)
 *    · expired → review (#FDDC69/#684E00 yellow). No hardcoded hex. */
export const DISCOUNT_STATUS_CHIP: Record<DiscountStatus, { label: string; intent: ChipIntent }> = {
  active: { label: 'Active', intent: 'approved' },
  inactive: { label: 'Inactive', intent: 'attention' },
  expired: { label: 'Expired', intent: 'review' },
};

export const TRIGGER_LABEL: Record<TriggerType, string> = {
  once_per_customer: 'Once Per Customer',
  limited_period: 'Limited Period',
  first_time_customer: 'First Time Customer',
};
export const QUANTITY_RULE_LABEL: Record<QuantityRuleType, string> = {
  fixed: 'Fixed Discount',
  quantity_based: 'Quantity Based',
};

/** Discount detail Customers tab — per-Customer redemption state (inline icon + text, not a
 *  `<Chip>`; see ticket 06 Figma). */
export const REDEMPTION_STATUS_LABEL: Record<RedemptionStatus, string> = {
  used: 'Used',
  not_used: 'Not Used',
};
export const FIRST_TIME_HELP = 'Available only to customers placing their first order';
export const BANNER_TITLE = { limited: 'Limited Time Offer', expired: 'Offer Expired' } as const;

/** Detail display date, `D MMM YYYY` (e.g. "22 May 2025") — matches the discount detail Figma
 *  (no comma). ET is the backend's concern (ADR-0007); the FE renders the date-only string as-is. */
export const formatDetailDate = (date: string): string => dayjs(date).format('D MMM YYYY');

/** Quantity-tier range, number-only ("1 - 5", "20+"); the "units" suffix is a separate muted
 *  span in the row (Figma weights the range and the suffix differently). */
export const formatTierRange = (t: DiscountTier): string =>
  t.maxQty == null ? `${t.minQty}+` : `${t.minQty} - ${t.maxQty}`;
export const formatFlexibleDiscount = (max: number, format: DiscountFormat): string =>
  `Maximum discount is limited to ${formatDiscountValue(max, format)} across all quantity tiers.`;

type BannerInput = {
  status: DiscountStatus;
  trigger: { type: TriggerType; dateRange: { from: string; to: string } | null };
};
export type TriggerBanner = { variant: 'limited' | 'expired'; description: string } | null;

/** Trigger/status banner (110055): Expired wins; Once-Per-Customer/Limited-Period with a window →
 *  "Limited Time Offer" (wording differs per type); no window / First-Time → no banner. */
export function getDiscountBanner({ status, trigger }: BannerInput): TriggerBanner {
  const r = trigger.dateRange;
  if (status === 'expired' && r)
    return {
      variant: 'expired',
      description: `This offer expired on ${formatDetailDate(r.to)} and is no longer available to customers`,
    };
  if (trigger.type === 'limited_period' && r)
    return {
      variant: 'limited',
      description: `Triggers on ${formatDetailDate(r.from)}, and auto-expires at midnight on ${formatDetailDate(r.to)}.`,
    };
  if (trigger.type === 'once_per_customer' && r)
    return {
      variant: 'limited',
      description: `This one-time discount is available from ${formatDetailDate(r.from)} and auto-expires at midnight ${formatDetailDate(r.to)}.`,
    };
  return null;
}

// --- Create-wizard overview formatters (ticket 10) ---
const NOUN = { device: 'Device', bundle: 'Bundle', customer: 'Customer' } as const;

function part(kind: 'device' | 'bundle' | 'customer', all: boolean, count: number): string {
  return all ? `All ${NOUN[kind]}s` : `${count} ${NOUN[kind]}(s)`;
}

function applicableToPart(a: {
  type: 'all_products' | 'device' | 'bundle';
  all: boolean;
  count: number;
}): string {
  return a.type === 'all_products' ? 'All Products' : part(a.type, a.all, a.count);
}

/** Mirrors the server `applicability.summary` phrasing so the live Create Overview matches the
 *  detail page before any round-trip (grilling Q8). `-` until ticket 10 wires the picker. */
export function formatApplicabilitySummary(a: Applicability | undefined): string {
  if (!a) return '-';
  if (a.type === 'customer') {
    return `${part('customer', a.all, a.count)} & ${applicableToPart(a.applicableTo)}`;
  }
  return part(a.type, a.all, a.count);
}

/** Map the in-form applicability value to the summary shape `formatApplicabilitySummary` consumes.
 *  Returns undefined (→ "-") until the sub-form is valid, so the Overview only shows a complete pair. */
export function toApplicabilitySummary(a: ApplicabilityFormValue): Applicability | undefined {
  if (!applicabilitySchema.safeParse(a).success) return undefined;
  if (a.type === 'customer') {
    const at = a.applicableTo;
    const applicableTo =
      at.type === 'all_products'
        ? ({ type: 'all_products', all: false, count: 0 } as const)
        : at.type === 'device'
          ? { type: 'device' as const, all: at.devices.all, count: at.devices.ids.length }
          : { type: 'bundle' as const, all: at.bundles.all, count: at.bundles.ids.length };
    return { type: 'customer', all: a.customers.all, count: a.customers.ids.length, applicableTo };
  }
  const pv = a.type === 'device' ? a.devices : a.bundles;
  return { type: a.type, all: pv.all, count: pv.ids.length };
}

const fmtWindow = ([from, to]: [string, string]): string =>
  `${dayjs(from).format('D MMM YYYY')} - ${dayjs(to).format('D MMM YYYY')}`;

/** Overview "Trigger": the trigger type, plus its date window when one is set (grilling Q8/Q11.3).
 *  Returned as two parts so the overview can put the window on its own line. */
export function formatTrigger(t: TriggerFormValue): { label: string; window: string | null } {
  if (t.type === 'first_time_customer') return { label: 'First Time Customer', window: null };
  // `dateRange` can be transiently null right after selecting Limited Period (before a pick), so
  // both windowed triggers guard it — the Next-gate/schema still require Limited's window.
  const label = t.type === 'limited_period' ? 'Limited Period' : 'Once Per Customer';
  return { label, window: t.dateRange ? fmtWindow(t.dateRange) : null };
}

/** Overview "Amount": flat → shared `formatPrice` (e.g. `$57.00`), percentage → `10%`.
 *  `-` when empty/non-numeric. */
export function formatDiscountAmount(format: DiscountFormat | undefined, value: string): string {
  if (!value || !format) return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return format === 'percentage' ? `${value}%` : formatPrice(n);
}
